import React from 'react';
import Typography from './Typography/Typography';
import { withClassName } from './cn';
import { useErrorsSummarised } from './errorSummaryScope';

/**
 * The helper line under a field — a hint, or the error that replaces it — in ONE
 * place (§ 302).
 *
 * Eight controls render this line, and until 2026-09-15 they rendered it eight
 * times. Four of them (`TextField`, `Textarea`, `DatePicker`, `TimePicker`) had
 * learned the announcement lesson: `aria-describedby` alone is read only when the
 * field TAKES focus, so a submit-time error appearing while focus was elsewhere
 * announced nothing (WCAG 4.1.3 / 3.3.1); the message therefore carries
 * `role="alert"` while errored, and is REMOUNTED on the hint→error transition (the
 * `key`) because an alert announces on insertion — toggling the role on an element
 * already in the DOM is not reliably announced by any screen reader. The other four
 * (`Dropdown`, `Combobox`, `Search`, `FileUpload`) had not, so one form could hold a
 * TextField whose error was heard and a Dropdown whose error was not.
 *
 * A seam rather than a component: it renders no chrome of its own, takes the
 * field's spacing through `className`, and owns exactly the two decisions every
 * field had been making separately — the ink pair, and when the line is live.
 * `id` is the field's `aria-describedby` target; the caller still composes that.
 */
export interface FieldHelperProps {
  /** The id the control's `aria-describedby` points at. */
  id: string;
  /** The hint, or the error message. */
  text: React.ReactNode;
  /** While true the line is `role="alert"` in the danger ink, and remounts so it announces. */
  error?: boolean;
  /** Lands on the message element — the field's own spacing (`tw-mt-1`), if any. */
  className?: string;
  'data-testid'?: string;
}

export const FieldHelper = ({
  id,
  text,
  error = false,
  className = '',
  'data-testid': dataTestId,
}: FieldHelperProps) => {
  /* § 365 — inside an `ErrorSummaryScope` the summary is the one announcement: the line keeps
     its text, ink and id (so `aria-describedby` still reads it with the field), not the role. */
  const summarised = useErrorsSummarised();
  return (
    <Typography
      key={error ? 'error' : 'hint'}
      role={error && !summarised ? 'alert' : undefined}
      id={id}
      variant="caption-metadata"
      className={withClassName(error ? 'tw-text-tone-danger-ink' : 'tw-text-ink-tertiary', className)}
      data-testid={dataTestId}
    >
      {text}
    </Typography>
  );
};

/** A message worth rendering — the empty string, `null`, `false` and `undefined` are not. */
export const hasMessage = (node: React.ReactNode): boolean =>
  node !== undefined && node !== null && node !== false && node !== '';

/**
 * § 356 — a field is in error when it is told so (`error`) OR when it is given the error
 * text (`errorMessage`). Every field's border, `aria-invalid` and ring read this.
 */
export const fieldInvalid = (error: boolean | undefined, errorMessage: React.ReactNode): boolean =>
  Boolean(error) || hasMessage(errorMessage);

/**
 * § 356 — the ids the control's `aria-describedby` gains from its messages: the error
 * first (the more urgent fact), then the hint. Absent lines contribute nothing.
 */
export const messageIds = (
  ids: { helperId: string; errorId: string },
  { helperText, errorMessage }: { helperText?: React.ReactNode; errorMessage?: React.ReactNode },
): Array<string | undefined> => [
  hasMessage(errorMessage) ? ids.errorId : undefined,
  hasMessage(helperText) ? ids.helperId : undefined,
];

export interface FieldMessagesProps {
  helperId: string;
  errorId: string;
  /** The hint. Without `errorMessage` it doubles as the error text while `error` is true — the legacy shape. */
  helperText?: React.ReactNode;
  /** § 356 — the error text, on its own line, so the hint is not lost when the field errs. */
  errorMessage?: React.ReactNode;
  error?: boolean;
  /** The field's own spacing for the FIRST line (`tw-mt-1` …); the second sits 2px under it. */
  className?: string;
  /** Templates `${id}-error` onto the error line. */
  'data-testid'?: string;
}

/**
 * § 356 — the lines under a field. Until now a field had ONE line: the hint, or — while
 * `error` was true — the hint re-inked as the error, so the moment a field erred the user
 * lost the sentence that told them what it wanted. With `errorMessage` the hint stays, in
 * its own ink, and the error takes a line of its own (live, via `FieldHelper`). Without
 * `errorMessage` this renders exactly what every field rendered before.
 */
export const FieldMessages = ({
  helperId,
  errorId,
  helperText,
  errorMessage,
  error = false,
  className = '',
  'data-testid': dataTestId,
}: FieldMessagesProps) => {
  const showError = hasMessage(errorMessage);
  const showHint = hasMessage(helperText);
  if (!showError && !showHint) return null;
  return (
    <>
      {showHint && (
        <FieldHelper id={helperId} text={helperText} error={error && !showError} className={className} />
      )}
      {showError && (
        <FieldHelper
          id={errorId}
          text={errorMessage}
          error
          /* Under a hint it is a BLOCK. `caption-metadata` renders an inline `<span>`, and two
             inline spans share one line box, so the error ran on from the end of the hint
             ("…work address.That address is taken") and its margin did nothing, since an
             inline element takes no vertical margin. jsdom has no layout; the browser suite
             caught it. Alone, it is the one inline line every field always rendered. */
          className={showHint ? 'tw-block tw-mt-0.5' : className}
          data-testid={dataTestId ? `${dataTestId}-error` : undefined}
        />
      )}
    </>
  );
};
